import { defineStore } from 'pinia'
import { ref } from 'vue'
import axiosInstance from '@/helpers/axios'
import { SwalSuccess, SwalError, SwalDelete } from '@/helpers/mySwal'
import { useJobStore } from './job'

export const usePaymentStore = defineStore('payment', () => {
  const jobStore = useJobStore()

  const payments = ref([])
  const payment = ref({})
  const loading = ref(false)
  const isLoaded = ref(false)
  const isLoadedOne = ref(false)

  const paymentMethodOptions = ref(['מזומן', 'העברה', 'כ. אשראי', 'אחר'])

  const getAllPayments = async () => {
    if (loading.value) {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      await getAllPayments()
    } else if (!isLoaded.value) {
      await readAllPayments()
    }
  }

  const readAllPayments = async () => {
    loading.value = true
    try {
      const { data } = await axiosInstance.get('/api/payments')
      payments.value = data
      isLoaded.value = true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת תשלומים נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const getOnePayment = async (id) => {
    loading.value = true
    try {
      const { data } = await axiosInstance.get(`/api/payments/${id}`)
      payment.value = data
      isLoadedOne.value = true
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'טעינת תשלום נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const createPayment = async (data) => {
    loading.value = true
    try {
      const { data: payment } = await axiosInstance.post('/api/payments', data)
      if (isLoaded.value) {
        payments.value.push(payment)
      }

      jobStore.isLoaded = false

      SwalSuccess.fire({
        title: 'תשלום חדש נוצר בהצלחה'
      })
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'הוספת תשלום נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const updatePayment = async (data) => {
    loading.value = true
    try {
      const { data: updatedPayment } = await axiosInstance.put(`/api/payments/${data.id}`, data)

      if (isLoaded.value) {
        payments.value = payments.value.map((payment) =>
          payment.id === data.id ? updatedPayment : payment
        )
      }

      jobStore.isLoaded = false

      if (isLoadedOne.value) {
        payment.value = updatedPayment
      }

      SwalSuccess.fire({
        title: 'פרטי התשלום עודכנו בהצלחה'
      })
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'עדכון תשלום נכשל'
      })
    } finally {
      loading.value = false
    }
  }

  const deletePayment = async (id) => {
    loading.value = true
    try {
      await axiosInstance.delete(`/api/payments/${id}`)
      if (isLoaded.value) {
        payments.value = payments.value.filter((payment) => payment.id !== id)
      }

      jobStore.isLoaded = false

      SwalSuccess.fire({
        title: 'פרטי התשלום נמחקו בהצלחה'
      })
    } catch (error) {
      console.error(error)
      SwalError.fire({
        title: 'מחיקת תשלום נכשלה'
      })
    } finally {
      loading.value = false
    }
  }

  const confirmDelete = async (id) => {
    const confirm = await SwalDelete.fire({
      text: `האם אתה בטוח שברצונך למחוק את התשלום?`
    })

    if (confirm.isConfirmed) {
      await deletePayment(id)
    }
  }

  const $reset = () => {
    payments.value = []
    payment.value = {}
    loading.value = false
    isLoaded.value = false
    isLoadedOne.value = false
  }

  return {
    payments,
    payment,
    loading,
    isLoaded,
    isLoadedOne,
    paymentMethodOptions,
    getAllPayments,
    getOnePayment,
    createPayment,
    updatePayment,
    deletePayment,
    confirmDelete,
    $reset
  }
})
